import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { CHURCH } from '@/api/client';
import Logo from '@/components/Logo';
import { LogOut, Plus, Users, ListChecks, Calendar } from 'lucide-react';
import { toast } from 'sonner';

export default function TeamsDashboard() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [leader, setLeader] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    const t = localStorage.getItem('moj_team_token');
    if (!t) nav('/teams');
    try {
      setLeader(JSON.parse(localStorage.getItem('moj_team_leader') || 'null'));
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Could not parse stored team leader profile:', err);
      }
      localStorage.removeItem('moj_team_leader');
    }
  }, [nav]);

  const { data: members = [] } = useQuery({ queryKey: ['team-members'], queryFn: async () => (await api.get('/teams/members')).data, enabled: !!leader });
  const { data: tasks = [] } = useQuery({ queryKey: ['team-tasks'], queryFn: async () => (await api.get('/teams/tasks')).data, enabled: !!leader });

  const empty = () => ({ title: '', description: '', department: leader?.department || '', assigned_to: '', assigned_date: new Date().toISOString().slice(0,10), due_date: '', notes: '' });
  const [form, setForm] = useState(empty());

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setForm(empty()); }, [leader]);

  const create = useMutation({
    mutationFn: async (p) => (await api.post('/teams/tasks', p)).data,
    onSuccess: () => { toast.success('Task assigned'); qc.invalidateQueries({ queryKey: ['team-tasks'] }); setShowForm(false); setForm(empty()); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not assign'),
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }) => (await api.put(`/teams/tasks/${id}`, { status })).data,
    onSuccess: () => { toast.success('Updated'); qc.invalidateQueries({ queryKey: ['team-tasks'] }); },
  });

  const cancel = useMutation({
    mutationFn: async (id) => (await api.delete(`/teams/tasks/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['team-tasks'] }); },
  });

  const logout = () => { localStorage.removeItem('moj_team_token'); localStorage.removeItem('moj_team_leader'); nav('/teams'); };

  if (!leader) return null;

  return (
    <div className="min-h-screen bg-moj-cream">
      <header className="gradient-purple text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <Logo size="md" light/>
            <div>
              <h1 className="font-serif text-xl">Team Leader — {leader.department}</h1>
              <div className="text-xs text-moj-gold">Welcome, {leader.full_name}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-white/80 hover:text-moj-gold">View site</Link>
            <button onClick={logout} className="btn-gold text-sm py-2 px-3" data-testid="team-logout"><LogOut size={14}/> Sign out</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="card-moj p-5"><Users size={26} className="text-moj-gold-deep mb-2"/><div className="text-3xl font-bold text-moj-purple-deep">{members.length}</div><div className="text-sm text-moj-charcoal/70">Approved Team Members</div></div>
          <div className="card-moj p-5"><ListChecks size={26} className="text-moj-gold-deep mb-2"/><div className="text-3xl font-bold text-moj-purple-deep">{tasks.filter(t => t.status !== 'Completed' && t.status !== 'Cancelled').length}</div><div className="text-sm text-moj-charcoal/70">Active Tasks</div></div>
          <div className="card-moj p-5"><Calendar size={26} className="text-moj-gold-deep mb-2"/><div className="text-3xl font-bold text-moj-purple-deep">{tasks.filter(t => t.status === 'Completed').length}</div><div className="text-sm text-moj-charcoal/70">Completed Tasks</div></div>
        </div>

        <div className="card-moj p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-serif text-lg text-moj-purple-deep">Team Members</h3>
          </div>
          {members.length === 0 ? (
            <p className="text-sm text-moj-charcoal/70">No approved members yet. Once members volunteer for {leader.department} and are approved by admin/pastor, they’ll appear here.</p>
          ) : (
            <div className="divide-y">
              {members.map((m) => (
                <div key={m.profile_id} className="py-3" data-testid={`team-member-${m.profile_id}`}>
                  <div className="font-semibold text-moj-purple-deep">{m.full_name}</div>
                  <div className="text-xs text-moj-charcoal/60">{m.email} • {m.phone || 'No phone'}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card-moj p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-serif text-lg text-moj-purple-deep">Tasks</h3>
            <button onClick={() => setShowForm(!showForm)} className="btn-gold text-sm py-2 px-3" data-testid="new-task-btn" disabled={members.length === 0}><Plus size={14}/> Assign Task</button>
          </div>
          {showForm && (
            <form onSubmit={(e) => { e.preventDefault(); if (!form.assigned_to) return toast.error('Select a member'); create.mutate(form); }} className="grid sm:grid-cols-2 gap-3 mb-5 p-4 bg-moj-cream rounded-lg" data-testid="task-form">
              <input required className="input-moj sm:col-span-2" placeholder="Task title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}/>
              <select required className="input-moj" value={form.assigned_to} onChange={(e) => setForm({...form, assigned_to: e.target.value})} data-testid="task-assignee">
                <option value="">Select member…</option>
                {members.map((m) => <option key={m.profile_id} value={m.profile_id}>{m.full_name}</option>)}
              </select>
              <input className="input-moj" placeholder="Department" value={form.department} readOnly/>
              <input required type="date" className="input-moj" value={form.assigned_date} onChange={(e) => setForm({...form, assigned_date: e.target.value})}/>
              <input required type="date" className="input-moj" value={form.due_date} onChange={(e) => setForm({...form, due_date: e.target.value})}/>
              <textarea rows={3} className="input-moj sm:col-span-2" placeholder="Description" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}/>
              <input className="input-moj sm:col-span-2" placeholder="Notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}/>
              <button type="submit" disabled={create.isPending} className="btn-primary sm:col-span-2 justify-center" data-testid="task-submit">Assign Task</button>
            </form>
          )}

          {tasks.length === 0 ? (
            <p className="text-sm text-moj-charcoal/60">No tasks yet.</p>
          ) : (
            <div className="space-y-2">
              {tasks.map((t) => (
                <div key={t.id} className="flex flex-wrap items-center gap-3 p-3 bg-moj-cream rounded-lg" data-testid={`tl-task-${t.id}`}>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-semibold text-moj-purple-deep">{t.title}</div>
                    <div className="text-xs text-moj-charcoal/60">Assigned to {t.assigned_to_name} • Due {t.due_date}</div>
                  </div>
                  <select value={t.status} onChange={(e) => updateStatus.mutate({ id: t.id, status: e.target.value })} className="input-moj text-xs py-1 px-2">
                    {['Assigned','Accepted','In Progress','Completed','Cancelled'].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {t.status !== 'Cancelled' && t.status !== 'Completed' && <button onClick={() => cancel.mutate(t.id)} className="text-xs text-red-600 hover:underline">Cancel</button>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
